import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";
import { useUserStore } from "@/stores/user";
import MainLayout from "@/layouts/MainLayout.vue";
import AuthLayout from "@/layouts/AuthLayout.vue";
import { setupRouterGuard } from "@/router/guard";

type AdminAccessTarget = {
  path: string;
  permissions?: string[];
  roles?: string[];
};

const adminAccessTargets: AdminAccessTarget[] = [
  {
    path: "/home/permission",
    permissions: [
      "permission_manage",
      "permission_read_update",
      "permission_read",
      "permission_view",
      "permissions_read",
      "permissions_view",
    ],
  },
  { path: "/home/task-registry", permissions: ["task_registry_read"] },
  { path: "/home/api-test", permissions: ["api_test_read"] },
  {
    path: "/home/broker-etf-qualification",
    permissions: ["broker_etf_qualification_read"],
  },
  { path: "/home/system/k8s-scaling", permissions: ["k8s_scaling_read"] },
  {
    path: "/home/ops/system-observation",
    permissions: ["ops_system_observation_read"],
  },
  {
    path: "/home/ops/persona-decision",
    permissions: ["ops_persona_decision_read"],
  },
  {
    path: "/home/ops/notification-center",
    permissions: ["notification_center_manage"],
  },
  { path: "/home/ops/easyetf-management", roles: ["admin"] },
  { path: "/home/ops/easyetf-feedback", roles: ["admin"] },
  { path: "/home/ops/etf-fund-info", permissions: ["ops_etf_fund_info_read"] },
  {
    path: "/home/ops/trader-actions",
    permissions: ["ops_trader_actions_read"],
  },
  {
    path: "/home/ops/minisignal-whitelist",
    permissions: ["ops_minisignal_whitelist_manage"],
  },
  {
    path: "/home/etf-cashdif-limit-pnl",
    permissions: ["etf_cashdif_limit_pnl_read"],
  },
  {
    path: "/home/main-variety-basis-threshold",
    permissions: ["main_variety_basis_threshold_read_update"],
  },
  {
    path: "/home/exposure-map-management",
    permissions: ["exposure_map_read", "exposure_map_update"],
  },
  { path: "/home/report-center", permissions: ["report_center_read"] },
  { path: "/home/reports/arbitrage-daily", permissions: ["report_daily_view"] },
  { path: "/home/reports/weekly-statement-email", roles: ["admin"] },
  {
    path: "/home/daily-report/etf-validator",
    permissions: ["daily_report_etf_validator_read"],
  },
];

// 登录后默认落地页:优先系统观测大盘,无权限时回退到首个可访问页
const PREFERRED_DEFAULT_PATH = "/home/ops/system-observation";

const resolveDefaultAdminPath = () => {
  const userStore = useUserStore();

  if (!userStore.isAuthenticated) {
    return adminAccessTargets[0].path;
  }

  const canAccess = (item: AdminAccessTarget) => {
    if (item.roles?.some((role) => userStore.hasRole(role))) {
      return true;
    }
    if (item.permissions?.length) {
      return userStore.hasPermission(item.permissions);
    }
    return false;
  };

  const preferred = adminAccessTargets.find(
    (item) => item.path === PREFERRED_DEFAULT_PATH,
  );
  if (preferred && canAccess(preferred)) {
    return preferred.path;
  }

  const target = adminAccessTargets.find(canAccess);

  return target?.path || adminAccessTargets[0].path;
};

const adminChildren: RouteRecordRaw[] = [
  { path: "", redirect: resolveDefaultAdminPath },
  // 兼容 Cookpit 通用首页链接，进入 admin 后落到当前用户可访问的默认页面。
  { path: "landing", redirect: resolveDefaultAdminPath },
  {
    path: "permission",
    name: "权限管理",
    component: () => import("@/views/Permission.vue"),
    meta: {
      title: "权限管理",
      requireAuth: true,
      permissions: [
        "permission_manage",
        "permission_read_update",
        "permission_read",
        "permission_view",
        "permissions_read",
        "permissions_view",
      ],
    },
  },
  {
    path: "task-registry",
    name: "任务注册表",
    component: () => import("@/views/system/TaskRegistry.vue"),
    meta: {
      title: "任务注册表",
      requireAuth: true,
      permissions: ["task_registry_read"],
    },
  },
  {
    path: "task-registry/:name",
    name: "任务详情",
    component: () => import("@/views/system/TaskRegistryDetail.vue"),
    meta: {
      title: "任务详情",
      requireAuth: true,
      permissions: ["task_registry_read"],
    },
  },
  {
    path: "api-test",
    name: "API测试",
    component: () => import("@/views/system/ApiTest.vue"),
    meta: {
      title: "API测试",
      requireAuth: true,
      permissions: ["api_test_read"],
    },
  },
  {
    path: "broker-etf-qualification",
    name: "券商ETF资质",
    component: () => import("@/views/system/BrokerEtfQualification.vue"),
    meta: {
      title: "券商ETF资质管理",
      requireAuth: true,
      permissions: ["broker_etf_qualification_read"],
    },
  },
  {
    path: "system/k8s-scaling",
    name: "K8sScaling",
    component: () => import("@/views/system/K8sScaling.vue"),
    meta: {
      title: "K8s 伸缩管理",
      requireAuth: true,
      permissions: ["k8s_scaling_read"],
    },
  },
  {
    path: "system/k8s-scaling/:configName",
    name: "K8sScalingDetail",
    component: () => import("@/views/system/K8sScalingDetail.vue"),
    meta: {
      title: "伸缩组详情",
      requireAuth: true,
      permissions: ["k8s_scaling_read"],
    },
  },
  {
    path: "system/k8s-scaling-logs",
    name: "K8sScalingLogs",
    component: () => import("@/views/system/K8sScalingLogs.vue"),
    meta: {
      title: "伸缩操作日志",
      requireAuth: true,
      permissions: ["k8s_scaling_read"],
    },
  },
  {
    path: "ops/system-observation",
    name: "系统观测大盘",
    component: () => import("@/views/ops/SystemObservation.vue"),
    meta: {
      title: "系统观测大盘",
      requireAuth: true,
      permissions: ["ops_system_observation_read"],
    },
  },
  {
    path: "ops/persona-decision",
    name: "人物决策",
    component: () => import("@/views/ops/PersonaDecision.vue"),
    meta: {
      title: "人物决策",
      requireAuth: true,
      permissions: ["ops_persona_decision_read"],
    },
  },
  {
    path: "ops/notification-center",
    name: "通知中心",
    component: () => import("@/views/ops/NotificationCenter.vue"),
    meta: {
      title: "通知中心",
      requireAuth: true,
      permissions: ["notification_center_manage"],
    },
  },
  {
    path: "ops/easyetf-management",
    name: "EasyETF管理",
    component: () => import("@/views/ops/EasyEtfManagement.vue"),
    meta: { title: "EasyETF管理", requireAuth: true, roles: ["admin"] },
  },
  {
    path: "ops/easyetf-feedback",
    name: "EasyETF反馈",
    component: () => import("@/views/ops/EasyEtfFeedback.vue"),
    meta: {
      title: "EasyETF反馈",
      requireAuth: true,
      roles: ["admin"],
    },
  },
  {
    path: "ops/etf-fund-info",
    name: "ETF基金管理",
    component: () => import("@/views/ops/EtfFundInfo.vue"),
    meta: {
      title: "ETF基金管理",
      requireAuth: true,
      permissions: ["ops_etf_fund_info_read"],
    },
  },
  {
    path: "ops/trader-actions",
    name: "交易员动作",
    component: () => import("@/views/ops/TraderActions.vue"),
    meta: {
      title: "交易员动作",
      requireAuth: true,
      permissions: ["ops_trader_actions_read"],
    },
  },
  {
    path: "ops/minisignal-whitelist",
    name: "Mini-Signal白名单",
    component: () => import("@/views/ops/MinisignalWhitelist.vue"),
    meta: {
      title: "Mini-Signal白名单",
      requireAuth: true,
      permissions: ["ops_minisignal_whitelist_manage"],
    },
  },
  {
    path: "etf-cashdif-limit-pnl",
    name: "ETF现金偏差与涨跌停盈亏",
    component: () => import("@/views/ops/EtfCashdifLimitPnl.vue"),
    meta: {
      title: "ETF现金偏差与涨跌停盈亏监控",
      requireAuth: true,
      permissions: ["etf_cashdif_limit_pnl_read"],
    },
  },
  {
    path: "main-variety-basis-threshold",
    name: "主品种基差阈值",
    component: () =>
      import("@/views/basis-management/MainVarietyBasisThreshold.vue"),
    meta: {
      title: "主品种基差阈值管理",
      requireAuth: true,
      permissions: ["main_variety_basis_threshold_read_update"],
    },
  },
  {
    path: "exposure-map-management",
    name: "对冲映射管理",
    component: () =>
      import("@/views/basis-management/exposure-map/ExposureMapManagement.vue"),
    meta: {
      title: "对冲映射管理",
      requireAuth: true,
      permissions: ["exposure_map_read", "exposure_map_update"],
    },
  },
  {
    path: "report-center",
    name: "报告中心",
    component: () => import("@/views/reports/ReportCenter.vue"),
    meta: {
      title: "报告中心",
      requireAuth: true,
      permissions: ["report_center_read"],
    },
  },
  {
    path: "reports/arbitrage-daily",
    name: "套利交易日报",
    component: () => import("@/views/reports/ArbitrageDailyReport.vue"),
    meta: {
      title: "套利交易日报",
      requireAuth: true,
      permissions: ["report_daily_view"],
    },
  },
  {
    path: "reports/weekly-statement-email",
    name: "周度对账单邮件",
    component: () => import("@/views/reports/WeeklyStatementEmail.vue"),
    meta: {
      title: "周度对账单邮件",
      requireAuth: true,
      roles: ["admin"],
    },
  },
  {
    path: "daily-report/etf-validator",
    name: "ETF变动日报",
    component: () => import("@/views/daily-report/EtfValidatorReport.vue"),
    meta: {
      title: "ETF变动日报",
      requireAuth: true,
      permissions: ["daily_report_etf_validator_read"],
    },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    { path: "/", redirect: resolveDefaultAdminPath },
    {
      path: "/login",
      component: AuthLayout,
      meta: {
        title: "登录",
        requireAuth: false,
      },
      children: [
        {
          path: "",
          name: "登录",
          component: () => import("@/views/Login.vue"),
          meta: {
            title: "登录",
            requireAuth: false,
          },
        },
      ],
    },
    {
      path: "/home",
      component: MainLayout,
      meta: { requireAuth: true },
      children: adminChildren,
    },
    {
      path: "/:pathMatch(.*)*",
      name: "NotFound",
      component: () => import("@/views/NotFound.vue"),
      meta: {
        title: "页面未找到",
        requireAuth: false,
      },
    },
  ],
});

export const menus = [
  {
    icon: "Lock",
    name: "权限",
    children: [{ path: "/home/permission", icon: "Lock", name: "权限管理" }],
  },
  {
    icon: "Setting",
    name: "系统管理",
    children: [
      { path: "/home/task-registry", icon: "Timer", name: "任务注册表" },
      {
        path: "/home/system/k8s-scaling",
        icon: "Monitor",
        name: "K8s 伸缩管理",
      },
      {
        path: "/home/system/k8s-scaling-logs",
        icon: "Document",
        name: "伸缩操作日志",
      },
      { path: "/home/api-test", icon: "Connection", name: "API测试" },
      {
        path: "/home/broker-etf-qualification",
        icon: "Document",
        name: "券商ETF资质",
      },
    ],
  },
  {
    icon: "Memo",
    name: "运维中心",
    children: [
      {
        path: "/home/ops/system-observation",
        icon: "Odometer",
        name: "系统观测大盘",
      },
      { path: "/home/ops/persona-decision", icon: "View", name: "人物决策" },
      { path: "/home/ops/notification-center", icon: "Bell", name: "通知中心" },
      {
        path: "/home/ops/easyetf-management",
        icon: "Memo",
        name: "EasyETF管理",
      },
      {
        path: "/home/daily-report/etf-validator",
        icon: "DataAnalysis",
        name: "ETF变动日报",
      },
      {
        path: "/home/ops/etf-fund-info",
        icon: "Management",
        name: "ETF基金管理",
      },
      {
        path: "/home/ops/trader-actions",
        icon: "Operation",
        name: "交易员动作",
      },
      {
        path: "/home/etf-cashdif-limit-pnl",
        icon: "DataLine",
        name: "ETF现金偏差与涨跌停",
      },
    ],
  },
  {
    icon: "DataLine",
    name: "基差配置",
    children: [
      {
        path: "/home/main-variety-basis-threshold",
        icon: "SetUp",
        name: "主品种基差阈值",
      },
      {
        path: "/home/exposure-map-management",
        icon: "DataLine",
        name: "对冲映射管理",
      },
    ],
  },
  {
    icon: "Document",
    name: "报告中心",
    children: [
      { path: "/home/report-center", icon: "Files", name: "报告中心" },
      {
        path: "/home/reports/arbitrage-daily",
        icon: "DataAnalysis",
        name: "套利交易日报",
      },
      {
        path: "/home/reports/weekly-statement-email",
        icon: "Document",
        name: "周度对账单邮件",
      },
    ],
  },
];

setupRouterGuard(router);

export default router;
